import * as Sentry from "@sentry/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import {
  Outlet,
  useParams,
  useRouteError,
  useSearchParams,
} from "react-router-dom"
import { getSession, getToken, logoutSession } from "@/api/api"
import {
  AppError,
  ExpiredTokenError,
  InvalidAuthorizationCodeError,
} from "@/api/errors"
import { ErrorMessage } from "@/components/error-message"
import { PageLoader } from "@/components/page-loader"
import { RequestNewInvitation } from "@/components/request-new-invitation"
import { cacheInvitationMapping } from "@/lib/invitation-cache"
import { useIsMounted } from "@/lib/use-is-mounted"

const SessionLayout = () => {
  const isMounted = useIsMounted()
  const queryClient = useQueryClient()

  const [searchParams, setSearchParams] = useSearchParams()
  const params = useParams<{ token_id: string }>()
  const token_id = params.token_id as string
  const code = searchParams.get("code")

  const sessionQuery = useQuery({
    queryKey: ["session", token_id],
    queryFn: () => getSession(),
    enabled: !code,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const tokenMutation = useMutation({
    mutationKey: ["getToken", { token_id, code }],
    mutationFn: getToken,
    onSuccess: (data) => {
      cacheInvitationMapping(token_id, data.invitation_id)
      queryClient.setQueryData(["session", token_id], data)
      setSearchParams()
    },
  })

  useEffect(() => {
    if (isMounted() && code && tokenMutation.isIdle) {
      tokenMutation.mutate({ token_id, code })
    }
  }, [code, isMounted, tokenMutation, token_id])

  if (tokenMutation.isPending || (!code && sessionQuery.isPending)) {
    return <PageLoader />
  }

  if (tokenMutation.isError) {
    throw tokenMutation.error
  }

  if (sessionQuery.isError) {
    throw sessionQuery.error
  }

  if (tokenMutation.isSuccess || sessionQuery.isSuccess) {
    return <Outlet />
  }

  return <PageLoader />
}

const ErrorBoundary = () => {
  const error = useRouteError()
  const params = useParams<{ token_id: string }>()
  const token_id = params.token_id as string

  useEffect(() => {
    if (error instanceof ExpiredTokenError) {
      // Best-effort cookie hygiene: tell the backend to clear the session.
      logoutSession().catch(() => {
        // ignore — we're already in the error path
      })
    }
  }, [error])

  useEffect(() => {
    if (!error) return
    if (error instanceof ExpiredTokenError) {
      Sentry.addBreadcrumb({
        category: "auth",
        message: "ExpiredTokenError surfaced in SessionLayout",
        level: "info",
      })
      return
    }
    Sentry.captureException(error, {
      tags: {
        errorBoundary: "SessionLayout",
        errorType: error instanceof AppError ? "AppError" : "UnknownError",
      },
      extra: {
        errorName: error instanceof Error ? error.name : "Unknown",
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        errorTitle: error instanceof AppError ? error.title : undefined,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
      level: "error",
    })
  }, [error])

  if (!(error instanceof AppError)) {
    return (
      <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
        <ErrorMessage refresh>Error inesperado</ErrorMessage>
      </div>
    )
  }

  const action =
    error instanceof InvalidAuthorizationCodeError && error.resendToken ? (
      <RequestNewInvitation
        tokenId={token_id}
        resendToken={error.resendToken}
        signeeEmailHint={error.signeeEmailHint}
      />
    ) : undefined

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
      <ErrorMessage title={error.title} action={action}>
        {error.message}
      </ErrorMessage>
    </div>
  )
}

export { ErrorBoundary, SessionLayout }
